/** Live cost computation helpers — frontend mirror of zbc_engine.py.
 * Used for running totals while the user is editing the form. */

const SCRAP_RECOVERABLE = 0.96;
const ICC_PCT = 1.0;
const REJECTION_PCT = 0.25;
const TOOL_MAINT_PCT = 1.0;
const PROFIT_OH_PCT = 5.0;

const num = (v) => parseFloat(v) || 0;
const round2 = (v) => Math.round(v * 100) / 100;

/** Cutting rate lookup — round UP thickness to next master rate. */
export function findCuttingRate(thickness, processRates) {
    const candidates = processRates
        .filter((r) => r.process_type === "CUTTING" && r.is_active)
        .sort((a, b) => a.thickness_or_fillet - b.thickness_or_fillet);
    for (const r of candidates) {
        if (thickness <= r.thickness_or_fillet) return { rate: r.rate, used: r.thickness_or_fillet };
    }
    if (candidates.length) {
        const last = candidates[candidates.length - 1];
        return { rate: last.rate, used: last.thickness_or_fillet };
    }
    return { rate: 0, used: thickness };
}

/** Welding rate lookup — round UP fillet to next master rate (Rule 4). */
export function findWeldingRate(fillet, processRates) {
    const candidates = processRates
        .filter((r) => r.process_type === "WELDING" && r.is_active)
        .sort((a, b) => a.thickness_or_fillet - b.thickness_or_fillet);
    for (const r of candidates) {
        if (fillet <= r.thickness_or_fillet) return { rate: r.rate, used: r.thickness_or_fillet };
    }
    if (candidates.length) {
        const last = candidates[candidates.length - 1];
        return { rate: last.rate, used: last.thickness_or_fillet };
    }
    return { rate: 0, used: fillet };
}

export function computeOpCost(op) {
    const qty = num(op.qty_or_time);
    const rate = num(op.rate);
    switch (op.uom) {
        case "PER_HOUR": case "PER_KG": case "PER_PIECE": case "PER_STROKE":
            return round2(qty * rate);
        case "PER_BOX":
            return round2(rate / (num(op.parts_per_box) || 1));
        case "LUMP":
            return round2(rate);
        default:
            return 0;
    }
}

/** Returns the full computed breakdown given the form state + master rates. */
export function computeZbcLive(form, gradeRate, scrapRate, processRates) {
    const rm = form.rm_category;
    if (rm === "BOP_PURCHASED") {
        const bop = num(form.bop_price);
        // CAPEX + children still apply to BOP items
        const toolingCost = num(form.tooling_cost_rs);
        const annualVol = num(form.annual_volume);
        const amortYrs = num(form.amortization_years) || 3;
        const capexPerPiece = (toolingCost > 0 && annualVol > 0 && amortYrs > 0)
            ? round2(toolingCost / (annualVol * amortYrs)) : 0;
        let childrenCost = 0;
        for (const ch of (form.child_zbc_refs || [])) {
            childrenCost += num(ch.child_should_cost) * (num(ch.qty) || 1);
        }
        childrenCost = round2(childrenCost);
        return {
            gross_material_cost: 0, scrap_credit: 0, net_material_cost: bop,
            conversion_cost: 0, cutting_cost: 0, welding_cost: 0,
            base_cost: bop, icc_amount: 0, rejection_amount: 0,
            tool_maint_amount: 0, profit_oh_amount: 0, allowance_total: 0,
            capex_per_piece: capexPerPiece, children_cost: childrenCost,
            should_cost: round2(bop + capexPerPiece + childrenCost),
        };
    }

    // Material
    let gross, scrapCredit;
    const rmMode = (form.rm_entry_mode || "CALCULATE").toUpperCase();
    if (rm === "FILL_MATERIAL") {
        gross = (form.fill_materials || []).reduce((s, fm) => s + num(fm.qty) * num(fm.rate), 0);
        scrapCredit = 0;
    } else if (rmMode === "LUMP") {
        // Direct lump-sum RM cost (Volvo SUMPRODUCT path) — bypass weight × rate
        gross = num(form.rm_lump_cost);
        scrapCredit = 0;
    } else {
        const iw = num(form.input_weight_kg);
        const fw = num(form.finish_weight_kg);
        gross = iw * gradeRate;
        const scrapW = Math.max(0, iw - fw);
        scrapCredit = scrapW * SCRAP_RECOVERABLE * scrapRate;
    }
    const netMaterial = round2(gross - scrapCredit);

    // Filling material (Section 2B — counterweight shells)
    let fillingCost = 0;
    let fillingWeight = 0;
    if (form.filling_material_enabled) {
        for (const fc of (form.filling_components || [])) {
            const qty = num(fc.qty ?? fc.weight_kg);
            const rate = num(fc.rate ?? fc.rate_rs_per_kg);
            fillingCost += qty * rate;
            fillingWeight += qty;
        }
    }
    fillingCost = round2(fillingCost);
    fillingWeight = round2(fillingWeight);

    // Conversion
    const conversion = round2(
        (form.operations || []).reduce((s, op) => s + computeOpCost(op), 0),
    );

    // Cutting
    let cutting = 0;
    if ((rm === "SHEET_STEEL" || rm === "SQUARE_TUBE") && num(form.cut_perimeter_mtr) && num(form.thickness_mm)) {
        const { rate } = findCuttingRate(num(form.thickness_mm), processRates);
        cutting = round2(num(form.cut_perimeter_mtr) * num(form.thickness_mm) * rate);
    }

    // Welding (round UP per joint)
    let welding = 0;
    if (form.has_welding) {
        for (const j of (form.weld_joints || [])) {
            const fillet = num(j.fillet_mm);
            const { rate } = findWeldingRate(fillet, processRates);
            welding += num(j.length_mtr) * rate * (parseInt(j.qty, 10) || 1);
        }
    }
    welding = round2(welding);

    // Allowances — include filling in base AND ICC (mirrors backend compute_zbc)
    const base = round2(netMaterial + fillingCost + conversion + cutting + welding);
    const icc = round2((netMaterial + fillingCost) * ICC_PCT / 100);
    const rejection = round2(base * REJECTION_PCT / 100);
    const toolMaint = round2(base * TOOL_MAINT_PCT / 100);
    const poh = round2(base * PROFIT_OH_PCT / 100);
    const allowanceTotal = round2(icc + rejection + toolMaint + poh);

    // P0.2 · CAPEX per piece
    const toolingCost = num(form.tooling_cost_rs);
    const annualVol = num(form.annual_volume);
    const amortYrs = num(form.amortization_years) || 3;
    let capexPerPiece = 0;
    if (toolingCost > 0 && annualVol > 0 && amortYrs > 0) {
        capexPerPiece = round2(toolingCost / (annualVol * amortYrs));
    }

    // P0.3 · Children roll-up
    let childrenCost = 0;
    for (const ch of (form.child_zbc_refs || [])) {
        childrenCost += num(ch.child_should_cost) * (num(ch.qty) || 1);
    }
    childrenCost = round2(childrenCost);

    const should = round2(base + allowanceTotal + capexPerPiece + childrenCost);

    return {
        gross_material_cost: round2(gross),
        scrap_credit: round2(scrapCredit),
        net_material_cost: netMaterial,
        total_filling_cost_rs: fillingCost,
        total_fill_weight_kg: fillingWeight,
        conversion_cost: conversion,
        cutting_cost: cutting,
        welding_cost: welding,
        base_cost: base,
        icc_amount: icc,
        rejection_amount: rejection,
        tool_maint_amount: toolMaint,
        profit_oh_amount: poh,
        allowance_total: allowanceTotal,
        capex_per_piece: capexPerPiece,
        children_cost: childrenCost,
        should_cost: should,
    };
}

export function computeVariance(should, supplierQuote) {
    const q = num(supplierQuote);
    if (!q || !should) return { amount: null, pct: null, badge: null };
    const amount = round2(q - should);
    const pct = Math.round((amount / should) * 1000) / 10;
    let badge;
    if (pct < 0) badge = "FLAG";
    else if (pct < 5) badge = "GREEN";
    else if (pct < 15) badge = "AMBER";
    else badge = "RED";
    return { amount, pct, badge };
}

/** Calculate input weight from sheet dimensions. */
export function sheetInputWeight(w, l, t) {
    return round2(num(w) * num(l) * num(t) * 7.85 / 1_000_000);
}

/** Round bar input weight. */
export function roundBarInputWeight(od, length) {
    return round2(3.14 * (num(od) ** 2) * num(length) * 7.85 / 4_000_000);
}
